import { VehicleOwnership } from '../../../__generated__/globalTypes';

import type { UpsertVehicleInput } from '../../../__generated__/globalTypes';
import type { IVehicle } from '../../../hooks/queries/useVehicles';

/** Card title from year + make + model, falling back to "Vehicle" when none are set. */
export const buildVehicleTitle = (year: number | null, make: string, model: string): string => {
  const parts = [year ? String(year) : null, make.trim() || null, model.trim() || null].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : 'Vehicle';
};

/** True when a draft vehicle has a make or model worth persisting (ownership is always pre-filled). */
export const hasVehicleDetails = (make: string, model: string): boolean =>
  make.trim().length > 0 || model.trim().length > 0;

/** True when the make / model / ownership edits match the row — no mutation needed. */
export const vehicleEditsUnchanged = (
  vehicle: IVehicle,
  make: string,
  model: string,
  ownership: VehicleOwnership | null
): boolean =>
  (make.trim() || null) === (vehicle.make ?? null) &&
  (model.trim() || null) === (vehicle.model ?? null) &&
  ownership === vehicle.ownership;

/** Build the `upsertVehicle` input for an existing row, carrying the edited make / model / ownership. */
export const buildVehicleUpsertInput = (
  vehicle: IVehicle,
  make: string,
  model: string,
  ownership: VehicleOwnership
): UpsertVehicleInput => ({
  vehicleId: vehicle.id,
  year: vehicle.year,
  make: make.trim() || null,
  model: model.trim() || null,
  ownership,
  ownershipOther: ownership === VehicleOwnership.Other ? vehicle.ownershipOther : null,
});

/** Build the create input for a brand-new (family-shared) vehicle, defaulting ownership. */
export const buildVehicleCreateInput = (
  make: string,
  model: string,
  ownership: VehicleOwnership | null
): UpsertVehicleInput => ({
  make: make.trim() || null,
  model: model.trim() || null,
  ownership: ownership ?? VehicleOwnership.OwnOutright,
  ownershipOther: null,
  year: null,
});
